import * as React from "react";
import {
  Box,
  Dialog,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloseRounded from "@mui/icons-material/CloseRounded";
import AddRounded from "@mui/icons-material/AddRounded";
import RemoveRounded from "@mui/icons-material/RemoveRounded";
import RestartAltRounded from "@mui/icons-material/RestartAltRounded";
import ZoomOutMapRounded from "@mui/icons-material/ZoomOutMapRounded";
import { useTheme } from "@mui/material/styles";

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

export default function ProductViewer({
  src,
  alt,
  title,
  children,
  openFrom = "click",
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [open, setOpen] = React.useState(false);

  const [scale, setScale] = React.useState(1);
  const [tx, setTx] = React.useState(0);
  const [ty, setTy] = React.useState(0);

  const containerRef = React.useRef(null);
  const imgRef = React.useRef(null);

  const draggingRef = React.useRef(false);
  const lastRef = React.useRef({ x: 0, y: 0 });

  const pointersRef = React.useRef(new Map());
  const pinchRef = React.useRef({
    active: false,
    startDist: 0,
    startScale: 1,
    startCenter: { x: 0, y: 0 },
    startTx: 0,
    startTy: 0,
  });

  const MIN_SCALE = 1;
  const MAX_SCALE = 4;

  const reset = React.useCallback(() => {
    setScale(1);
    setTx(0);
    setTy(0);
  }, []);

  const close = React.useCallback(() => {
    setOpen(false);
    setTimeout(() => reset(), 0);
  }, [reset]);

  const openViewer = React.useCallback(() => {
    if (!src) return;
    setOpen(true);
  }, [src]);

  const zoomBy = React.useCallback(
    (delta, origin = null) => {
      setScale((prev) => {
        const next = clamp(prev + delta, MIN_SCALE, MAX_SCALE);
        return next;
      });

      if (origin && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const ox = origin.clientX - rect.left - rect.width / 2;
        const oy = origin.clientY - rect.top - rect.height / 2;
        setTx((prevTx) => prevTx - ox * (delta * 0.2));
        setTy((prevTy) => prevTy - oy * (delta * 0.2));
      }
    },
    [MIN_SCALE, MAX_SCALE],
  );

  const onWheel = (e) => {
    e.preventDefault();
    const step = e.deltaY > 0 ? -0.2 : 0.2;
    zoomBy(step, e);
  };

  const onPointerDown = (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;

    e.currentTarget.setPointerCapture?.(e.pointerId);

    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointersRef.current.size === 1) {
      draggingRef.current = true;
      lastRef.current = { x: e.clientX, y: e.clientY };
    }

    if (pointersRef.current.size === 2) {
      const pts = Array.from(pointersRef.current.values());
      const dx = pts[0].x - pts[1].x;
      const dy = pts[0].y - pts[1].y;
      const dist = Math.hypot(dx, dy);
      const center = {
        x: (pts[0].x + pts[1].x) / 2,
        y: (pts[0].y + pts[1].y) / 2,
      };

      pinchRef.current = {
        active: true,
        startDist: dist,
        startScale: scale,
        startCenter: center,
        startTx: tx,
        startTy: ty,
      };
      draggingRef.current = false;
    }
  };

  const onPointerMove = (e) => {
    if (!open) return;

    if (pointersRef.current.has(e.pointerId)) {
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    }

    if (pointersRef.current.size === 2 && pinchRef.current.active) {
      const pts = Array.from(pointersRef.current.values());
      const dx = pts[0].x - pts[1].x;
      const dy = pts[0].y - pts[1].y;
      const dist = Math.hypot(dx, dy);
      const center = {
        x: (pts[0].x + pts[1].x) / 2,
        y: (pts[0].y + pts[1].y) / 2,
      };

      const ratio = dist / (pinchRef.current.startDist || dist);
      const nextScale = clamp(
        pinchRef.current.startScale * ratio,
        MIN_SCALE,
        MAX_SCALE,
      );
      setScale(nextScale);

      const dcx = center.x - pinchRef.current.startCenter.x;
      const dcy = center.y - pinchRef.current.startCenter.y;
      setTx(pinchRef.current.startTx + dcx);
      setTy(pinchRef.current.startTy + dcy);
      return;
    }

    if (draggingRef.current) {
      const dx = e.clientX - lastRef.current.x;
      const dy = e.clientY - lastRef.current.y;
      lastRef.current = { x: e.clientX, y: e.clientY };

      if (scale > 1) {
        setTx((p) => p + dx);
        setTy((p) => p + dy);
      }
    }
  };

  const onPointerUpOrCancel = (e) => {
    pointersRef.current.delete(e.pointerId);

    if (pointersRef.current.size < 2) {
      pinchRef.current.active = false;
    }
    if (pointersRef.current.size === 0) {
      draggingRef.current = false;
    }
  };

  const onDoubleClick = (e) => {
    e.preventDefault();
    setScale((prev) => {
      const next = prev < 1.6 ? 2 : 1;
      if (next === 1) {
        setTx(0);
        setTy(0);
      }
      return next;
    });
  };

  React.useEffect(() => {
    if (!open) return;
    const maxShift = 600 * (scale - 1);
    setTx((v) => clamp(v, -maxShift, maxShift));
    setTy((v) => clamp(v, -maxShift, maxShift));
  }, [scale, open]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "+" || e.key === "=") zoomBy(0.2);
      if (e.key === "-" || e.key === "_") zoomBy(-0.2);
      if (e.key.toLowerCase() === "r") reset();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close, zoomBy, reset]);

  const Trigger = children ? (
    <Box
      onClick={openFrom === "click" ? openViewer : undefined}
      sx={{ cursor: openFrom === "click" ? "zoom-in" : "default" }}
      role={openFrom === "click" ? "button" : undefined}
      tabIndex={openFrom === "click" ? 0 : undefined}
      onKeyDown={(e) => {
        if (openFrom !== "click") return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openViewer();
        }
      }}
      aria-label={openFrom === "click" ? "Open image viewer" : undefined}
    >
      {children}
    </Box>
  ) : (
    <Box
      onClick={openFrom === "click" ? openViewer : undefined}
      sx={{
        width: "100%",
        height: "100%",
        cursor: openFrom === "click" ? "zoom-in" : "default",
      }}
      role={openFrom === "click" ? "button" : undefined}
      tabIndex={openFrom === "click" ? 0 : undefined}
      onKeyDown={(e) => {
        if (openFrom !== "click") return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openViewer();
        }
      }}
      aria-label={openFrom === "click" ? "Open image viewer" : undefined}
    >
      <img
        src={src}
        alt={alt}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          display: "block",
        }}
      />
    </Box>
  );

  return (
    <>
      {Trigger}

      <Dialog
        open={open}
        onClose={close}
        fullScreen={fullScreen}
        maxWidth={false}
        PaperProps={{
          sx: {
            bgcolor: "rgba(10,10,12,0.92)",
            backdropFilter: "blur(10px)",
            width: "100vw",
            height: "100dvh",
            m: 0,
            borderRadius: fullScreen ? 0 : 4,
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 3,
            p: { xs: 1, sm: 1.5 },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            background:
              "linear-gradient(to bottom, rgba(0,0,0,0.65), rgba(0,0,0,0))",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                color: "rgba(255,255,255,0.92)",
                fontWeight: 800,
                textShadow: "0 2px 10px rgba(0,0,0,0.35)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "70vw",
              }}
            >
              {title || alt || "Image"}
            </Typography>
          </Box>

          <Tooltip title="Close (Esc)">
            <IconButton
              onClick={close}
              sx={{
                color: "white",
                bgcolor: "rgba(255,255,255,0.10)",
                border: "1px solid rgba(255,255,255,0.18)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.16)" },
              }}
              aria-label="Close image viewer"
            >
              <CloseRounded />
            </IconButton>
          </Tooltip>
        </Box>

        <Box
          sx={{
            position: "absolute",
            right: { xs: 10, sm: 18 },
            bottom: { xs: 16, sm: 20 },
            zIndex: 3,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Tooltip title="Zoom in (+)">
            <IconButton
              onClick={() => zoomBy(0.2)}
              sx={{
                color: "white",
                bgcolor: "rgba(255,255,255,0.10)",
                border: "1px solid rgba(255,255,255,0.18)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.16)" },
              }}
              aria-label="Zoom in"
            >
              <AddRounded />
            </IconButton>
          </Tooltip>

          <Tooltip title="Zoom out (-)">
            <IconButton
              onClick={() => zoomBy(-0.2)}
              sx={{
                color: "white",
                bgcolor: "rgba(255,255,255,0.10)",
                border: "1px solid rgba(255,255,255,0.18)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.16)" },
              }}
              aria-label="Zoom out"
            >
              <RemoveRounded />
            </IconButton>
          </Tooltip>

          <Tooltip title="Reset (R)">
            <IconButton
              onClick={reset}
              sx={{
                color: "white",
                bgcolor: "rgba(255,255,255,0.10)",
                border: "1px solid rgba(255,255,255,0.18)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.16)" },
              }}
              aria-label="Reset zoom"
            >
              <RestartAltRounded />
            </IconButton>
          </Tooltip>
        </Box>

        <Box
          sx={{ position: "absolute", inset: 0, zIndex: 1 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) close();
          }}
        />

        <Box
          ref={containerRef}
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: { xs: 2, sm: 4 },
            touchAction: "none",
          }}
          onWheel={onWheel}
          onDoubleClick={onDoubleClick}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUpOrCancel}
          onPointerCancel={onPointerUpOrCancel}
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: "100%",
              maxWidth: "1200px",
              maxHeight: "900px",
              borderRadius: fullScreen ? 0 : 4,
              overflow: "hidden",
              boxShadow: "0 24px 70px rgba(0,0,0,0.55)",
              border: "1px solid rgba(255,255,255,0.10)",
              bgcolor: "rgba(255,255,255,0.03)",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                left: 14,
                bottom: 14,
                zIndex: 4,
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.25,
                py: 0.75,
                borderRadius: 999,
                bgcolor: "rgba(0,0,0,0.35)",
                border: "1px solid rgba(255,255,255,0.12)",
                color: "rgba(255,255,255,0.85)",
                backdropFilter: "blur(10px)",
              }}
            >
              <ZoomOutMapRounded fontSize="small" />
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                {Math.round(scale * 100)}%
              </Typography>
            </Box>

            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                userSelect: "none",
              }}
            >
              <img
                ref={imgRef}
                src={src}
                alt={alt}
                draggable={false}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  transform: `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`,
                  transformOrigin: "center center",
                  transition:
                    pointersRef.current.size === 0
                      ? "transform 120ms ease-out"
                      : "none",
                  willChange: "transform",
                  objectFit: "contain",
                  cursor: scale > 1 ? "grab" : "zoom-in",
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                }}
              />
            </Box>
          </Box>
        </Box>
      </Dialog>
    </>
  );
}
